'use client';

import { DONUT_CHART_COLORS } from '@/modules/rankings/ppr/constants';

interface DonutChartProgressProps {
  totalPoints: number;
  doublesPoints: number;
  mixedPoints: number;
  singlesPoints: number;
}

const DISPLAY_PCT = { Singles: 15, Mixed: 35, Doubles: 50 } as const;

export function DonutChartProgress({
  totalPoints,
  doublesPoints,
  mixedPoints,
  singlesPoints,
}: DonutChartProgressProps) {
  const colors = DONUT_CHART_COLORS;

  const size = 220;
  const center = size / 2;
  const radius = 110;
  const strokeWidth = 50;
  const normalizedRadius = radius - strokeWidth / 2;
  const circumference = normalizedRadius * 2 * Math.PI;

  const toPct = (n: number) => (totalPoints > 0 ? (n / totalPoints) * 100 : 0);

  const rawData = [
    {
      label: 'Singles' as const,
      arcPct: toPct(singlesPoints),
      value: singlesPoints,
      color: colors.singles,
    },
    {
      label: 'Mixed' as const,
      arcPct: toPct(mixedPoints),
      value: mixedPoints,
      color: colors.mixed,
    },
    {
      label: 'Doubles' as const,
      arcPct: toPct(doublesPoints),
      value: doublesPoints,
      color: colors.doubles,
    },
  ];

  // Compute the mid-angle of each segment so we can place its label/dot/value
  // box along the radial direction that points into the middle of that slice.
  let cumPct = 0;
  const data = rawData.map((seg) => {
    const midPct = cumPct + seg.arcPct / 2;
    cumPct += seg.arcPct;
    // 0% sits at 12 o'clock, progressing clockwise.
    const angle = (midPct / 100) * 2 * Math.PI - Math.PI / 2;
    return { ...seg, angle };
  });

  const dotR = radius - 20; // connector dot sits outside the value pill
  const kinkR = radius + 30; // where the diagonal meets the horizontal run
  const horizLen = 70; // length of horizontal portion of the leader
  const valueR = radius; // value pill sits ~3px below the connector dot
  // Offset the pill along the arc so it no longer sits exactly on the
  // segment's radial midline (and therefore in line with the leader/dot).
  const pillAngleOffset = Math.PI / 8;

  let currentOffset = 0;

  return (
    <div className='relative pt-2' style={{ width: size, height: size, overflow: 'visible' }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ overflow: 'visible' }}
      >
        {data.map((segment) => {
          const arcLen = (segment.arcPct / 100) * circumference;
          const strokeDasharray = `${arcLen} ${circumference}`;
          const strokeDashoffset = -currentOffset;
          currentOffset += arcLen;

          return (
            <circle
              key={`${segment.label}-circle`}
              cx={center}
              cy={center}
              r={normalizedRadius}
              fill='transparent'
              stroke={segment.color}
              strokeWidth={strokeWidth}
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              transform={`rotate(-90 ${center} ${center})`}
              style={{ transition: 'stroke-dashoffset 0.3s ease' }}
            />
          );
        })}
        {data.map((segment) => {
          if (segment.arcPct <= 0) return null;
          const cos = Math.cos(segment.angle);
          const sin = Math.sin(segment.angle);
          const dotX = center + dotR * cos;
          const dotY = center + dotR * sin;
          const kinkX = center + kinkR * cos;
          const kinkY = center + kinkR * sin;
          const isRight = cos >= 0;
          const horizEndX = isRight ? kinkX + horizLen : kinkX - horizLen;
          return (
            <g key={`${segment.label}-connector`}>
              <line x1={dotX} y1={dotY} x2={kinkX} y2={kinkY} stroke='#e5e7eb' strokeWidth={1} />
              <line
                x1={kinkX}
                y1={kinkY}
                x2={horizEndX}
                y2={kinkY}
                stroke='#e5e7eb'
                strokeWidth={1}
              />
              <circle cx={dotX} cy={dotY} r={3} fill='white' stroke='#d1d5db' strokeWidth={1} />
            </g>
          );
        })}
      </svg>

      <div className='absolute inset-0 flex flex-col items-center justify-center pointer-events-none'>
        <span className='text-gray-500 text-sm'>Total</span>
        <span className='text-gray-900 text-2xl font-semibold'>{totalPoints}</span>
      </div>

      {data.map((segment) => {
        if (segment.arcPct <= 0) return null;
        const cos = Math.cos(segment.angle);
        const sin = Math.sin(segment.angle);
        const kinkX = center + kinkR * cos;
        const kinkY = center + kinkR * sin;
        const isRight = cos >= 0;
        const horizEndX = isRight ? kinkX + horizLen : kinkX - horizLen;
        const pillAngle = segment.angle + pillAngleOffset;
        const valueX = center + valueR * Math.cos(pillAngle);
        const valueY = center + valueR * Math.sin(pillAngle);
        // Label sits above the horizontal leader, aligned with its outer end so
        // the text never runs back over the donut.
        const labelAnchorX = horizEndX;
        const labelTx = isRight ? '-100%' : '0%';

        return (
          <div key={`${segment.label}-label-group`}>
            <span
              className='absolute text-gray-800 text-sm whitespace-nowrap'
              style={{
                left: labelAnchorX,
                top: kinkY,
                transform: `translate(${labelTx}, calc(-100% - 4px))`,
              }}
            >
              {segment.label} {DISPLAY_PCT[segment.label]}%
            </span>
            <div
              className='absolute bg-white border border-gray-200 rounded-lg px-2 py-1 flex items-center shadow-sm'
              style={{
                left: valueX,
                top: valueY,
                transform: 'translate(-50%, -50%)',
              }}
            >
              <div
                className='w-2 h-2 rounded-full mr-2'
                style={{ backgroundColor: segment.color }}
              />
              <span className='text-gray-800 font-semibold text-sm'>{segment.value}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
